import { QueryHookOptions } from '@apollo/client';
import React from 'react';
import { OperationData, OperationVariables, gql } from '@ts-gql/tag';
import { useQuery } from '@ts-gql/apollo';

import { TaskStatusEnum } from '../components/work/types/task-status-enum';
import { getTaskStatus } from '../globals';
import { parseSearchTasksVariables } from '../utils/data-utils';
import {
  useTenantTimezoneConversion,
  utcToTenantToLocalDateFunction,
} from '../hooks/use-tenant-timezone';

export const SEARCH_TASKS_QUERY = gql`
  query SearchTasks(
    $clients: [String!] = []
    $entities: [String!] = []
    $jobs: [String!] = []
    $myWork: Boolean! = false
    $dueDateOverdue: Boolean = false
    $staff: [String!] = []
    $statuses: [Float!] = []
    $dueDateBegin: DateTime = null
    $dueDateEnd: DateTime = null
    $completedDateBegin: DateTime = null
    $completedDateEnd: DateTime = null
    $familyGroupIds: [String!] = []
    $text: String = null
    $take: Float = null
    $excludeClosedJobTasks: Boolean = false
  ) {
    searchTasks(
      clients: $clients
      entities: $entities
      jobs: $jobs
      myWork: $myWork
      staff: $staff
      statuses: $statuses
      dueDateBegin: $dueDateBegin
      dueDateEnd: $dueDateEnd
      dueDateOverdue: $dueDateOverdue
      completedDateBegin: $completedDateBegin
      completedDateEnd: $completedDateEnd
      familyGroupIds: $familyGroupIds
      text: $text
      take: $take
      excludeClosedJobTasks: $excludeClosedJobTasks
    ) {
      ...SearchTasks_Task
    }
  }
` as import('../../__generated__/ts-gql/SearchTasks').type;

export const SearchTasksWithJobFragment = gql`
  fragment SearchTasks_Task on Task {
    cloudId
    jobId
    assignedToUserId
    dueDate
    completedDate
    entityId
    name
    status
    taskTypeId
    estimateMinutes
    description
    wfDeletedOnUTC
  }
` as import('../../__generated__/ts-gql/SearchTasks_Task').type;

export type SearchTasksQueryVariables = OperationVariables<
  typeof SEARCH_TASKS_QUERY
>;
export type SearchTasksQueryResult = OperationData<typeof SEARCH_TASKS_QUERY>;

export type SearchTaskItem = NonNullable<
  ReturnType<typeof processData>
>[number];

export const useSearchTasks = (
  options: QueryHookOptions<SearchTasksQueryResult, SearchTasksQueryVariables>
) => {
  const variables = options.variables as SearchTasksQueryVariables;

  const { localDateToTenantToUtc, utcToTenantToLocalDate, loading } =
    useTenantTimezoneConversion();

  const res = useQuery(SEARCH_TASKS_QUERY, {
    context: { service: 'core' },
    ...options,
    variables: parseSearchTasksVariables(variables, localDateToTenantToUtc),
    skip: options.skip || loading,
  });

  return React.useMemo(() => {
    if (loading) {
      return {
        loading: true,
        data: undefined,
        error: undefined,
        refetch: undefined,
      };
    }
    return {
      ...res,
      data: processData(res.data, utcToTenantToLocalDate),
    };
  }, [res, utcToTenantToLocalDate, loading]);
};

export function processData(
  data: SearchTasksQueryResult | undefined,
  dateConvert: utcToTenantToLocalDateFunction
) {
  const raw = data?.searchTasks;
  const results =
    raw &&
    raw.map((task) => ({
      ...task,
      // the ternary for status is to please the compiler. Tasks MUST have statuses, and we've retrieved the status
      status: task.status
        ? getTaskStatus(task.status)
        : TaskStatusEnum.valuesEnum.NotStarted,
      statusOrderIndex: task.status || 1,
      dueDate: task.dueDate && dateConvert(task.dueDate),
      completedDate: task.completedDate && new Date(task.completedDate),
      wfDeletedOnUTC: task.wfDeletedOnUTC && new Date(task.wfDeletedOnUTC),
    }));
  return results;
}
